import { useEffect, useRef } from "react";
import * as THREE from "three";
import { Bag, BUILDERS } from "./scenes.ts";
import type { SceneVariant } from "./themes.ts";

/** Time used for the single frame rendered under prefers-reduced-motion. */
const STILL_T = 3;

/**
 * Fills its (positioned) parent with a WebGL scene. Renders nothing when WebGL is unavailable.
 * Everything GPU-side is released on unmount or when the props change.
 */
export function GameScene({ variant, accent, accent2 = "#ff8a3d", className }: { variant: SceneVariant; accent: string; accent2?: string; className?: string }) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // The canvas is created here, not in JSX, so a remount never reuses a context we force-lost.
    const canvas = document.createElement("canvas");
    canvas.style.cssText = "position:absolute;inset:0;width:100%;height:100%;display:block";
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    } catch {
      return;
    }
    host.appendChild(canvas);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const root = new THREE.Group();
    scene.add(root);
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 200);
    const bag = new Bag();
    const rig = BUILDERS[variant]({ root, bag, a: new THREE.Color(accent), b: new THREE.Color(accent2) });

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const ptr = { x: 0, y: 0 };
    const target = { x: 0, y: 0 };
    let t = 0, last = 0, raf = 0, visible = !document.hidden, inView = true, lost = false;

    const render = () => {
      camera.position.set(rig.pos.x + ptr.x * 0.9, rig.pos.y - ptr.y * 0.5, rig.pos.z);
      camera.lookAt(rig.look);
      renderer.render(scene, camera);
    };
    const still = () => { rig.update(STILL_T, 0); render(); };

    const layout = () => {
      const w = host.clientWidth, h = host.clientHeight;
      if (!w || !h) return false;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      // Keep the subject right of centre where the hero copy leaves room for it.
      const dist = rig.pos.distanceTo(rig.look);
      const halfW = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * dist * camera.aspect;
      root.position.x = camera.aspect > 1.25 ? halfW * 0.34 * (rig.shift ?? 1) : 0;
      root.scale.setScalar(camera.aspect < 1 ? Math.max(0.4, camera.aspect * 1.1) : 1);
      return true;
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      t += dt;
      const k = Math.min(1, dt * 3);
      ptr.x += (target.x - ptr.x) * k;
      ptr.y += (target.y - ptr.y) * k;
      rig.update(t, dt);
      render();
    };

    const sync = () => {
      const run = visible && inView && !reduced.matches && !lost;
      if (run && !raf) { last = performance.now(); raf = requestAnimationFrame(frame); }
      else if (!run && raf) { cancelAnimationFrame(raf); raf = 0; }
    };

    const onPointer = (e: PointerEvent) => {
      target.x = (e.clientX / window.innerWidth) * 2 - 1;
      target.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    const onVisibility = () => { visible = !document.hidden; sync(); };
    const onReduced = () => { if (reduced.matches) { ptr.x = ptr.y = 0; if (!lost) still(); } sync(); };
    const onLost = (e: Event) => { e.preventDefault(); lost = true; sync(); };

    const ro = new ResizeObserver(() => { if (layout() && !raf && !lost) still(); });
    const io = new IntersectionObserver(([e]) => { inView = !!e?.isIntersecting; sync(); });
    ro.observe(host);
    io.observe(host);
    window.addEventListener("pointermove", onPointer, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    reduced.addEventListener("change", onReduced);
    canvas.addEventListener("webglcontextlost", onLost);

    if (layout()) still();
    sync();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener("pointermove", onPointer);
      document.removeEventListener("visibilitychange", onVisibility);
      reduced.removeEventListener("change", onReduced);
      canvas.removeEventListener("webglcontextlost", onLost);
      bag.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      canvas.remove();
    };
  }, [variant, accent, accent2]);

  return <div ref={hostRef} aria-hidden className={`pointer-events-none absolute inset-0 ${className ?? ""}`} />;
}
